import { Session } from "next-auth"
import { getSession } from "next-auth/react"
import { isAccessTokenExpired, isSessionExpired } from "./auth"

export const getCognitoAccessJwt = async () => {
  const session = await getSession()
  return session?.accessToken
}

// cache the session so we don't have to request it every time
let _session: Session | null = null

// currentSession should also be passed in, being provided via useSession and the SessionProvider
export const getCognitoAccessJwtCached = async (currentSession?: Session | null) => {
  // load from local storage for faster page load
  const accessTokenFromLocalStorage = getAccessTokenFromLocalStorage()
  if (accessTokenFromLocalStorage && !isAccessTokenExpired(accessTokenFromLocalStorage)) {
    return accessTokenFromLocalStorage
  }

  if (currentSession && !isSessionExpired(currentSession)) return currentSession.accessToken

  // not called from react-land, so we need to get the session
  // or it's expired
  if (!_session) {
    _session = await getSessionOnce()
  }
  if (_session?.accessToken && !isSessionExpired(_session)) return _session.accessToken
  const refreshedSession = await getSession()
  _session = refreshedSession
  return refreshedSession?.accessToken
}

// call getSession() only once even if called multiple times
let _sessionPromise: Promise<Session | null> | null = null
async function getSessionOnce() {
  if (!_sessionPromise) {
    _sessionPromise = getSession()

    // clear the promise after it resolves
    await _sessionPromise.then((gotSession) => {
      // cache it in local storage
      if (gotSession) setSessionInLocalStorage(gotSession)
      _sessionPromise = null
      return gotSession
    })
  }
  return _sessionPromise
}

function getAccessTokenFromLocalStorage() {
  if (typeof window === "undefined") return null
  const accessToken = localStorage.getItem("tombo.access-token") || null
  return accessToken
}

function setSessionInLocalStorage(session: Session) {
  if (typeof window === "undefined") return null
  localStorage.setItem("tombo.access-token", session.accessToken || "")
}

export const clearCachedJwt = () => {
  if (typeof window === "undefined") return

  _session = null
  localStorage.removeItem("tombo.access-token")
}
